/**
 * Formatos y escalas del panel de administración: montos en CLP y USD, números
 * compactos, variaciones contra el período anterior y ticks «limpios» para ejes.
 */

/** Pesos chilenos sin decimales: $6.990 */
export const clp = (n: number) => `$${Math.round(n).toLocaleString('es-CL')}`

/** Dólares con los decimales que el monto necesita (centavos pequeños incluidos). */
export function usd(n: number): string {
  const d = n !== 0 && Math.abs(n) < 1 ? 3 : 2
  return `US$ ${n.toLocaleString('es-CL', { minimumFractionDigits: d, maximumFractionDigits: d })}`
}

/** Dólares para un eje: siempre 2 decimales, para que los ticks se lean parejos. */
export const usdEje = (n: number) =>
  `US$ ${n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** 1.284 · 12,9 mil · 4,2 M */
export function compacto(n: number): string {
  const a = Math.abs(n)
  if (a >= 1_000_000)
    return `${(n / 1_000_000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} M`
  if (a >= 10_000) return `${(n / 1_000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} mil`
  return n.toLocaleString('es-CL', { maximumFractionDigits: 1 })
}

/** Porcentaje entero con espacio fino, como en español: «42 %». */
export const pct = (n: number, de: number) => (de > 0 ? `${Math.round((n / de) * 100)} %` : '—')

/**
 * Variación porcentual contra el período anterior. `null` si no hay base (el
 * período anterior fue 0): un «+∞ %» no informa nada.
 */
export function variacion(valor: number, anterior: number): number | null {
  if (anterior === 0) return valor === 0 ? 0 : null
  return ((valor - anterior) / anterior) * 100
}

/** Texto de una variación con signo: «+12 %», «−3 %», «nuevo». */
export function textoVariacion(v: number | null): string {
  if (v === null) return 'nuevo'
  const r = Math.round(v)
  if (r === 0) return '0 %'
  return `${r > 0 ? '+' : '−'}${Math.abs(r)} %`
}

/**
 * Tono de una variación según si subir es bueno (registros) o malo (costos). Un
 * cambio menor a 1 % es neutro.
 */
export function tonoVariacion(
  v: number | null,
  subirEsBueno: boolean
): 'bueno' | 'malo' | 'neutro' {
  if (v === null || Math.abs(v) < 1) return 'neutro'
  return v > 0 === subirEsBueno ? 'bueno' : 'malo'
}

/** Paso «limpio» (1, 2, 2,5, 5 × 10ⁿ) para que los ticks sean números redondos. */
export function pasoLimpio(bruto: number): number {
  if (bruto <= 0 || !Number.isFinite(bruto)) return 1
  const exp = Math.floor(Math.log10(bruto))
  const base = 10 ** exp
  const f = bruto / base
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10
  return nice * base
}

/** Ticks del eje Y de 0 a un máximo redondo que cubre `max`, con unas `n` divisiones. */
export function ticksY(max: number, n = 4): number[] {
  if (max <= 0) return [0, 1]
  const paso = pasoLimpio(max / n)
  const tope = Math.ceil(max / paso) * paso
  const out: number[] = []
  for (let v = 0; v <= tope + paso / 2; v += paso) out.push(Number(v.toPrecision(12)))
  return out
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic']

/** `2026-09-21` → «21 sept» (sin zona horaria: el día ya viene en hora de Chile). */
export function diaCorto(dia: string): string {
  const [, m, d] = dia.split('-').map(Number)
  return `${d} ${MESES[(m ?? 1) - 1]}`
}

/** Fecha y hora cortas en Chile: «21 sept, 10:14». */
export function fechaHora(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Santiago'
  })
}

/** Hace cuánto: «hoy», «ayer», «hace 5 días», «hace 3 meses». */
export function haceCuanto(iso: string | null, ahora = new Date()): string {
  if (!iso) return 'nunca'
  const dias = Math.floor((ahora.getTime() - new Date(iso).getTime()) / 86_400_000)
  if (dias <= 0) return 'hoy'
  if (dias === 1) return 'ayer'
  if (dias < 45) return `hace ${dias} días`
  const meses = Math.round(dias / 30)
  return meses < 12 ? `hace ${meses} meses` : `hace ${Math.round(dias / 365)} años`
}

/** Nombres legibles de las acciones de la bitácora. */
export const ACCION_LABEL: Record<string, string> = {
  admin_view_user: 'Abrió la ficha',
  admin_view_org: 'Abrió una organización',
  admin_list_users: 'Listó usuarios',
  admin_export_users: 'Exportó usuarios',
  admin_lookup: 'Buscó por correo',
  admin_credit: 'Regaló minutos',
  admin_plan: 'Cambió el plan',
  admin_cortesia: 'Dio una cortesía',
  cortesia_vencida: 'Venció una cortesía',
  admin_suspend: 'Suspendió la cuenta',
  admin_unsuspend: 'Reactivó la cuenta',
  admin_note: 'Escribió una nota',
  admin_delete_account: 'Borró la cuenta',
  admin_retry: 'Reintentó una grabación',
  admin_settings: 'Cambió ajustes',
  admin_discount_create: 'Creó un descuento',
  admin_discount_archive: 'Archivó un descuento'
}

export const accionLabel = (a: string) => ACCION_LABEL[a] ?? a.replace(/_/g, ' ')

export const ESTADO_USUARIO_LABEL = {
  sin_activar: 'Sin activar',
  activo: 'Activo',
  inactivo: 'Inactivo',
  suspendido: 'Suspendido'
} as const

const diaEnChile = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Santiago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
})

/** Hoy en Chile, `YYYY-MM-DD` (para el mínimo de un campo de fecha). */
export const hoyEnChile = (ahora = new Date()) => diaEnChile.format(ahora)

/**
 * Último segundo de un día `YYYY-MM-DD` en hora de Chile continental, con el horario
 * que rija ese día (UTC−3 en verano, UTC−4 en invierno).
 */
export function finDelDiaChile(dia: string): Date {
  for (const off of ['-03:00', '-04:00']) {
    const d = new Date(`${dia}T23:59:59${off}`)
    const hora = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'America/Santiago',
      hour: '2-digit',
      hourCycle: 'h23'
    }).format(d)
    if (diaEnChile.format(d) === dia && hora === '23') return d
  }
  return new Date(`${dia}T23:59:59-03:00`)
}
